import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLocation, Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicWebsite({ isPreview = false }) {
  const [loading, setLoading] = useState(true);
  const [websiteData, setWebsiteData] = useState(null);
  const [currentPage, setCurrentPage] = useState('home');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const fetchWebsite = async () => {
      try {
        const response = await axios.get(`${API}/public/website`);
        setWebsiteData(response.data);
        
        // Determine current page from URL
        const path = location.pathname.replace('/', '') || 'home';
        if (path !== 'preview') {
          setCurrentPage(path);
        }
      } catch (error) {
        console.error('Error fetching website:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWebsite();
  }, [location]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      await axios.post(`${API}/website/leads`, {
        lead_name: formData.get('name'),
        lead_email: formData.get('email'),
        lead_phone: formData.get('phone'),
        message: formData.get('message'),
        page: currentPage
      });
      setFormSubmitted(true);
      toast.success(websiteData?.contact_settings?.success_message || 'Thank you! We\'ll get back to you soon.');
      e.target.reset();
    } catch (error) {
      toast.error('Failed to submit form. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="public-website" style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        fontFamily: 'Lato, sans-serif'
      }}>
        Loading...
      </div>
    );
  }

  if (!websiteData) {
    return (
      <div className="public-website" style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        flexDirection: 'column',
        gap: '20px',
        fontFamily: 'Lato, sans-serif'
      }}>
        <h1>Website Not Published Yet</h1>
        <p>Please publish your website from the builder.</p>
        <Link to="/admin/website-builder" style={{
          padding: '12px 24px',
          background: '#8B7355',
          color: 'white',
          textDecoration: 'none',
          borderRadius: '4px'
        }}>
          Go to Website Builder
        </Link>
      </div>
    );
  }

  const { theme, pages, services, staff, testimonials, gallery, contact_settings, map_settings } = websiteData;
  const pageData = pages.find(p => p.page_key === currentPage) || pages.find(p => p.page_key === 'home');

  // Render a block
  const renderBlock = (block) => {
    if (block.visible === false) return null;

    switch (block.block_type) {
      case 'header':
        return (
          <header 
            key={block.block_id}
            className="salon-header"
            style={{ 
              background: theme?.header_style === 'dark' ? '#2c2c2c' : '#FAF8F5',
              color: theme?.header_style === 'dark' ? 'white' : '#2c2c2c'
            }}
          >
            <div 
              className="salon-logo"
              style={{ color: theme?.header_style === 'dark' ? 'white' : '#2c2c2c' }}
            >
              {block.content.salon_name || theme?.salon_name || 'Luxe Salon'}
            </div>
            <nav className="salon-nav">
              {(block.content.nav_items || ['Home', 'Services', 'Staff', 'About', 'Contact']).map((item, i) => (
                <Link 
                  key={i}
                  to={item.toLowerCase() === 'home' ? '/' : `/${item.toLowerCase()}`}
                  className="salon-nav-item"
                  style={{ 
                    color: currentPage === (item.toLowerCase() === 'home' ? 'home' : item.toLowerCase()) 
                      ? theme?.primary_color 
                      : (theme?.header_style === 'dark' ? '#ccc' : '#2c2c2c')
                  }}
                >
                  {item}
                </Link>
              ))}
            </nav>
          </header>
        );

      case 'hero':
        return (
          <div 
            key={block.block_id}
            className="salon-hero"
            style={{ 
              backgroundImage: `url(${block.content.background_image || 'https://images.unsplash.com/photo-1759134198561-e2041049419c?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85'})` 
            }}
          >
            <div className="hero-content">
              <h1 
                className="hero-title"
                style={{ fontFamily: `${theme?.heading_font || 'Cormorant Garamond'}, serif` }}
              >
                {block.content.heading || theme?.salon_name || 'Luxe Salon'}
              </h1>
              <p className="hero-subtitle">
                {block.content.subheading || theme?.tagline || 'Style & Beauty Redefined'}
              </p>
              <button 
                className="hero-cta"
                style={{ 
                  background: theme?.primary_color || '#8B7355',
                  borderRadius: theme?.button_style === 'pill' ? '50px' : (theme?.button_style === 'sharp' ? '0' : '4px')
                }}
                onClick={() => window.location.href = block.content.cta_link || '/booking'}
              >
                {block.content.cta_text || 'Book Appointment'}
              </button>
            </div>
          </div>
        );

      case 'services':
        return (
          <section key={block.block_id} className="salon-section" style={{ padding: theme?.section_spacing || '60px 40px' }}>
            <h2 
              className="section-title"
              style={{ fontFamily: `${theme?.heading_font || 'Playfair Display'}, serif` }}
            >
              {block.content.section_title || 'Our Services'}
            </h2>
            <div className="services-grid">
              {services.map(service => (
                <div 
                  key={service.id} 
                  className="service-card"
                  style={{ borderRadius: theme?.card_radius || '8px' }}
                >
                  <img 
                    src={service.image_url || 'https://images.unsplash.com/photo-1582095133179-bfd08e2fc6b3'} 
                    alt={service.service_name}
                    className="service-image"
                  />
                  <div className="service-content">
                    <div className="service-header">
                      <h3 className="service-name" style={{ fontFamily: `${theme?.heading_font || 'Playfair Display'}, serif` }}>
                        {service.service_name}
                      </h3>
                      <span className="service-price" style={{ color: theme?.primary_color }}>
                        ${service.price}
                      </span>
                    </div>
                    <p className="service-description">{service.description}</p>
                    {service.bookable && (
                      <button 
                        className="edit-btn"
                        style={{ 
                          background: theme?.primary_color || '#8B7355',
                          borderRadius: theme?.button_style === 'pill' ? '50px' : (theme?.button_style === 'sharp' ? '0' : '4px')
                        }}
                      >
                        Book Now
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        );

      case 'staff':
        return (
          <section 
            key={block.block_id} 
            className="salon-section alt"
            style={{ padding: theme?.section_spacing || '60px 40px' }}
          >
            <h2 
              className="section-title"
              style={{ fontFamily: `${theme?.heading_font || 'Playfair Display'}, serif` }}
            >
              {block.content.section_title || 'Meet Our Stylists'}
            </h2>
            <div className="staff-grid">
              {staff.map(member => (
                <div 
                  key={member.id} 
                  className="staff-card"
                  style={{ borderRadius: theme?.card_radius || '8px' }}
                >
                  <img 
                    src={member.photo_url || 'https://images.unsplash.com/photo-1602501025442-a4899b5e0b71'} 
                    alt={member.name}
                    className="staff-photo"
                  />
                  <h3 
                    className="staff-name"
                    style={{ fontFamily: `${theme?.heading_font || 'Playfair Display'}, serif` }}
                  >
                    {member.name}
                  </h3>
                  <p className="staff-title">{member.title}</p>
                  {member.show_booking_btn && (
                    <button 
                      className="edit-btn"
                      style={{ 
                        background: theme?.primary_color || '#8B7355',
                        borderRadius: theme?.button_style === 'pill' ? '50px' : (theme?.button_style === 'sharp' ? '0' : '4px')
                      }}
                    >
                      Book with {member.name.split(' ')[0]}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>
        );

      case 'testimonials':
        return (
          <section key={block.block_id} className="salon-section" style={{ padding: theme?.section_spacing || '60px 40px' }}>
            <div className="testimonial-section">
              <div className="staff-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
                {staff.slice(0, 3).map(member => (
                  <div key={member.id} className="staff-card" style={{ borderRadius: theme?.card_radius || '8px' }}>
                    <img 
                      src={member.photo_url || 'https://images.unsplash.com/photo-1602501025442-a4899b5e0b71'} 
                      alt={member.name}
                      className="staff-photo"
                      style={{ width: '120px', height: '120px' }}
                    />
                    <h3 className="staff-name" style={{ fontFamily: `${theme?.heading_font || 'Playfair Display'}, serif` }}>
                      {member.name}
                    </h3>
                    <p className="staff-title">{member.title}</p>
                  </div>
                ))}
              </div>
              
              {testimonials.length > 0 && (
                <div className="testimonial-card" style={{ borderRadius: theme?.card_radius || '8px' }}>
                  <span className="quote-mark" style={{ color: theme?.primary_color }}>"</span>
                  <p 
                    className="testimonial-quote"
                    style={{ fontFamily: `${theme?.heading_font || 'Cormorant Garamond'}, serif` }}
                  >
                    {testimonials[0].quote}
                  </p>
                  <p className="testimonial-author">- {testimonials[0].client_name}</p>
                  <span className="quote-mark end" style={{ color: theme?.primary_color }}>"</span>
                </div>
              )}
            </div>
          </section>
        );

      case 'gallery':
        return (
          <section key={block.block_id} className="salon-section alt" style={{ padding: theme?.section_spacing || '60px 40px' }}>
            <h2 
              className="section-title"
              style={{ fontFamily: `${theme?.heading_font || 'Playfair Display'}, serif` }}
            >
              {block.content.section_title || 'Gallery'}
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
              {gallery.map(img => (
                <img 
                  key={img.id}
                  src={img.image_url} 
                  alt={img.alt_text || 'Gallery image'}
                  style={{ 
                    width: '100%', 
                    height: '200px', 
                    objectFit: 'cover', 
                    borderRadius: theme?.card_radius || '8px' 
                  }}
                />
              ))}
            </div>
          </section>
        );

      case 'map':
        return (
          <section key={block.block_id} className="salon-section" style={{ padding: theme?.section_spacing || '60px 40px' }}>
            <h2 
              className="section-title"
              style={{ fontFamily: `${theme?.heading_font || 'Playfair Display'}, serif` }}
            >
              {block.content.title || map_settings?.location_title || 'Visit Us'}
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', alignItems: 'start' }}>
              <div style={{ 
                background: '#f5f5f5', 
                borderRadius: theme?.card_radius || '8px', 
                height: '300px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {map_settings?.map_embed ? (
                  <div dangerouslySetInnerHTML={{ __html: map_settings.map_embed }} />
                ) : (
                  <span style={{ color: '#888' }}>Map</span>
                )}
              </div>
              <div>
                <h3 style={{ fontFamily: `${theme?.heading_font || 'Playfair Display'}, serif`, marginBottom: '20px' }}>
                  {theme?.salon_name || 'Luxe Salon'}
                </h3>
                <p style={{ color: '#666', marginBottom: '12px', lineHeight: '1.6' }}>
                  {map_settings?.business_address || '123 Style Avenue, New York, NY 10001'}
                </p>
                <p style={{ color: '#666', marginBottom: '12px' }}>
                  {map_settings?.phone || '(555) 123-4567'}
                </p>
                <p style={{ color: '#666' }}>
                  {map_settings?.hours || 'Mon-Sat: 9AM-7PM, Sun: 10AM-5PM'}
                </p>
              </div>
            </div>
          </section>
        );

      case 'contact_form':
        return (
          <section 
            key={block.block_id} 
            className="salon-section alt"
            style={{ padding: theme?.section_spacing || '60px 40px' }}
          >
            <h2 
              className="section-title"
              style={{ fontFamily: `${theme?.heading_font || 'Playfair Display'}, serif` }}
            >
              {contact_settings?.form_title || 'Contact Us'}
            </h2>
            {formSubmitted ? (
              <div className="success-message">
                {contact_settings?.success_message || 'Thank you! We\'ll get back to you soon.'}
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} style={{ maxWidth: '600px' }}>
                {contact_settings?.show_name !== false && (
                  <div className="form-field">
                    <input type="text" name="name" placeholder="Your Name" required />
                  </div>
                )}
                {contact_settings?.show_email !== false && (
                  <div className="form-field">
                    <input type="email" name="email" placeholder="Email Address" required />
                  </div>
                )}
                {contact_settings?.show_phone !== false && (
                  <div className="form-field">
                    <input type="tel" name="phone" placeholder="Phone Number" />
                  </div>
                )}
                {contact_settings?.show_message !== false && (
                  <div className="form-field">
                    <textarea name="message" placeholder="Your Message" rows={4} />
                  </div>
                )}
                <button 
                  type="submit" 
                  className="form-submit-btn"
                  style={{ 
                    background: theme?.accent_color || '#5A9A5A',
                    borderRadius: theme?.button_style === 'pill' ? '50px' : (theme?.button_style === 'sharp' ? '0' : '4px')
                  }}
                >
                  {contact_settings?.submit_label || 'Send'}
                </button>
              </form>
            )}
          </section>
        );

      case 'text':
        return (
          <section key={block.block_id} className="salon-section" style={{ padding: theme?.section_spacing || '60px 40px' }}>
            <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#444' }}>
              {block.content.text}
            </p>
          </section>
        );

      case 'image':
        return (
          <section key={block.block_id} className="salon-section" style={{ padding: theme?.section_spacing || '60px 40px' }}>
            {block.content.url && (
              <img 
                src={block.content.url} 
                alt={block.content.alt || ''}
                style={{ maxWidth: '100%', borderRadius: theme?.card_radius || '8px' }}
              />
            )}
          </section>
        );

      case 'button':
        return (
          <section key={block.block_id} className="salon-section" style={{ padding: theme?.section_spacing || '60px 40px', textAlign: 'center' }}>
            <a 
              href={block.content.link || '#'}
              style={{
                display: 'inline-block',
                padding: '14px 32px',
                background: theme?.primary_color || '#8B7355',
                color: 'white',
                textDecoration: 'none',
                borderRadius: theme?.button_style === 'pill' ? '50px' : (theme?.button_style === 'sharp' ? '0' : '4px'),
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {block.content.text || 'Click Here'}
            </a>
          </section>
        );

      default:
        return null;
    }
  };

  // Sort blocks by order_index
  const sortedBlocks = pageData?.blocks ? [...pageData.blocks].sort((a, b) => a.order_index - b.order_index) : [];

  return (
    <div 
      className="public-website"
      style={{ 
        fontFamily: `${theme?.body_font || 'Lato'}, sans-serif`,
        minHeight: '100vh',
        background: 'white'
      }}
      data-testid="public-website"
    >
      {isPreview && (
        <div style={{
          position: 'fixed',
          top: '16px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#333',
          color: 'white',
          padding: '8px 20px',
          borderRadius: '20px',
          fontSize: '13px',
          zIndex: 1000
        }}>
          Preview Mode - <Link to="/admin/website-builder" style={{ color: '#5A9A5A' }}>Return to Editor</Link>
        </div>
      )}
      
      {sortedBlocks.map(block => renderBlock(block))}
      
      {/* Footer */}
      <footer style={{
        padding: '40px',
        background: '#2c2c2c',
        color: '#888',
        textAlign: 'center'
      }}>
        <p style={{ margin: '0 0 8px 0', fontFamily: `${theme?.heading_font || 'Playfair Display'}, serif`, color: 'white', fontSize: '20px' }}>
          {theme?.salon_name || 'Luxe Salon'}
        </p>
        <p style={{ margin: 0, fontSize: '13px' }}>
          &copy; {new Date().getFullYear()} {theme?.salon_name || 'Luxe Salon'}. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
